import { WidgetsService } from '@services/WidgetsService';
import { WidgetsSlice } from '@/store/reducers/WidgetsReducer';
import { LayoutItem, ResponsiveLayouts } from 'react-grid-layout';
import { IWidgetConfiguration } from '@models/widgets/widgetBuilders/IWidgetConfiguration';
import { WidgetBuilder } from '@models/widgets/widgetBuilders/WidgetBuilder';
import { IWidgetConf } from '@models/widgets/IWidgetConf';
import { IWidgetsData, WidgetsLayouts } from '@models/widgets/IWidgetsData';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { AppDispatch, RootState } from '@/store';
import { widgetSizes } from '@molecules/widgets/widgetSizes';

interface IWidgetSelection {
    builderClassId: string;
    name: string;
    initialConfig: Record<string, unknown>;
}

// Число колонок lg-сетки. Должно совпадать с RGL_COLS.lg в WidgetCanvas (квадратная сетка).
const LG_COLS = 40;

/** First-fit top-left: ищем самую верхнюю свободную позицию (с переездом вправо) */
function findFreePosition(items: LayoutItem[], cols: number, w: number, h: number): { x: number; y: number } {
    const maxY = items.reduce((m, it) => Math.max(m, it.y + it.h), 0);
    for (let y = 0; y <= maxY; y++) {
        for (let x = 0; x + w <= cols; x++) {
            const overlaps = items.some(
                (it) => x + w > it.x && it.x + it.w > x && y + h > it.y && it.y + it.h > y,
            );
            if (!overlaps) return { x, y };
        }
    }
    return { x: 0, y: maxY };
}

/** Возвращает layouts с добавленным элементом в lg-сетку (RGL сам спроецирует на остальные breakpoint'ы). */
function placeWidget(
    currentLayouts: WidgetsLayouts,
    builderClassId: string,
    widgetId: string,
): WidgetsLayouts {
    const size = widgetSizes[builderClassId];
    if (!size) return currentLayouts;
    const w = Math.min(Number.isFinite(size.w) ? size.w : LG_COLS, LG_COLS);
    const h = size.h;
    const arr: LayoutItem[] = currentLayouts.lg ? [...currentLayouts.lg] : [];
    const pos = findFreePosition(arr, LG_COLS, w, h);
    const newItem: LayoutItem = {
        i: widgetId,
        x: pos.x,
        y: pos.y,
        ...size,
        w,
        h,
    };
    return {
        ...currentLayouts,
        lg: [...arr, newItem],
    };
}

export const WidgetsActionCreators = {
    loadDashboard: () => async (dispatch: AppDispatch) => {
        try {
            dispatch(WidgetsSlice.actions.setIsLoading(true));
            const response = await WidgetsService.getData();
            dispatch(WidgetsSlice.actions.successLoadWidgetsData(response.data));
        } catch (e) {
            console.log(e);
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        } finally {
            dispatch(WidgetsSlice.actions.setIsLoading(false));
        }
    },
    saveDashboard: (data: IWidgetsData) => async (dispatch: AppDispatch) => {
        try {
            dispatch(WidgetsSlice.actions.setIsLoading(true));
            await WidgetsService.setData(data);
            // Фиксируем новый clean-baseline: lastSavedData = текущая data.
            dispatch(WidgetsSlice.actions.commitBaseline());
        } catch (e) {
            console.log(e);
            let error = e as string[];
            sendModalMsg({
                message: error,
                type: 'error',
            });
        } finally {
            dispatch(WidgetsSlice.actions.setIsLoading(false));
        }
    },
    // Выход из режима редактирования без save: откатываем data к серверной версии.
    // Используется везде, где раньше делался "bare" setDashboardIsEdit(false) — иначе
    // несохранённые изменения остаются в state.data и при повторном входе становятся
    // ложным baseline'ом.
    exitDashboardEditMode: () => async (dispatch: AppDispatch, getState: () => RootState) => {
        if (!getState().widgets.isEdit) return;
        dispatch(WidgetsSlice.actions.setIsEdit(false));
        await dispatch(WidgetsActionCreators.loadDashboard());
    },

    setLayouts: (layouts: ResponsiveLayouts) => async (dispatch: AppDispatch) => {
        dispatch(WidgetsSlice.actions.setLayouts(layouts as WidgetsLayouts));
    },
    setDashboardIsEdit: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(WidgetsSlice.actions.setIsEdit(flag));
    },
    addNewWidget:
        (builderClassId: string, name: string, initialConfig: Record<string, unknown>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            const newWidgetId = WidgetBuilder.generateWidgetId(builderClassId);
            const conf = {
                widgetId: newWidgetId,
                configuration: { name: name, ...initialConfig } as IWidgetConfiguration,
            } as IWidgetConf;
            const layouts = placeWidget(
                getState().widgets.data.layouts,
                builderClassId,
                newWidgetId,
            );
            dispatch(WidgetsSlice.actions.addWidgetWithLayout({ conf, layouts }));
        },
    addWidgetsAndSave:
        (selections: IWidgetSelection[]) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            try {
                dispatch(WidgetsSlice.actions.setIsLoading(true));
                selections.forEach(({ builderClassId, name, initialConfig }) => {
                    const newWidgetId = WidgetBuilder.generateWidgetId(builderClassId);
                    const conf = {
                        widgetId: newWidgetId,
                        configuration: { name, ...initialConfig } as IWidgetConfiguration,
                    } as IWidgetConf;
                    const layouts = placeWidget(
                        getState().widgets.data.layouts,
                        builderClassId,
                        newWidgetId,
                    );
                    dispatch(WidgetsSlice.actions.addWidgetWithLayout({ conf, layouts }));
                });
                const updatedData = getState().widgets.data;
                await WidgetsService.setData(updatedData);
                // После успешного autosave всегда фиксируем новый baseline (обновляем lastSavedData,
                // а в edit-режиме — и originalData). Иначе при добавлении виджетов через hero-экран
                // (isEdit=false) lastSavedData остаётся пустым, и последующий вход в режим настройки
                // считает уже сохранённые виджеты несохранёнными изменениями (ложный dirty).
                dispatch(WidgetsSlice.actions.commitBaseline());
            } catch (e) {
                console.log(e);
                sendModalMsg({
                    message: e as string[],
                    type: 'error',
                });
            } finally {
                dispatch(WidgetsSlice.actions.setIsLoading(false));
            }
        },
    removeWidget: (widgetId: string) => async (dispatch: AppDispatch) => {
        dispatch(WidgetsSlice.actions.removeWidgets(widgetId));
    },
    setWidgetConfiguration:
        (widgetId: string, widgetConfiguration: Record<string, any>) => async (dispatch: AppDispatch) => {
            const conf = {
                widgetId: widgetId,
                configuration: widgetConfiguration,
            } as IWidgetConf;
            dispatch(WidgetsSlice.actions.setWidgetConfiguration(conf));
        },
    /** Настройка виджета вне режима настройки дашборда сразу сохраняется на сервер, в режиме — ждёт «Сохранить». */
    saveWidgetConfiguration:
        (widgetId: string, widgetConfiguration: Record<string, any>) =>
        async (dispatch: AppDispatch, getState: () => RootState) => {
            await dispatch(WidgetsActionCreators.setWidgetConfiguration(widgetId, widgetConfiguration));
            const { isEdit, data } = getState().widgets;
            if (!isEdit) await dispatch(WidgetsActionCreators.saveDashboard(data));
        },
};
